import React, { useContext, useEffect, useRef } from 'react';
import styled from 'astroturf/react';
import { Button } from 'js/design_system/Button';
import { useToast } from 'js/design_system/toasts/hooks';
import { CiterightContext } from '../CiterightContextProvider';

const OverviewToastContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs-12);
  padding: var(--spacing-xs-8) var(--spacing-sm-24);
  background: var(--primary-brand);
  color: var(--text-white);
  border-radius: var(--border-radius-radius-sm);
  border: 1px solid var(--primary-brand);
  min-width: 300px;
`;

const ToastMessage = styled.span`
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-white);
  cursor: pointer;
  font-size: var(--type-size-sm-5);
  padding: var(--spacing-xs-4) var(--spacing-xs-8);
  line-height: 1;

  &:hover {
    opacity: 0.8;
  }
`;

/**
 * Custom hook that shows a toast notification when the overview report is ready.
 * Only shows once per component mount.
 *
 * @param setPage - Callback to navigate to the overview page when user clicks "View Overview"
 */
export const useOverviewReadyToast = (
  setPage: (page: 'overview') => void,
): void => {
  const context = useContext(CiterightContext);
  const { toast } = useToast();
  const hasShownToast = useRef(false);

  useEffect(() => {
    if (
      context?.report?.done &&
      context?.report?.overview_ready &&
      !hasShownToast.current
    ) {
      hasShownToast.current = true;
      const OverviewReadyToast = (_id: string, closeToast?: () => void) => (
        <OverviewToastContent>
          <ToastMessage>Your overview report is ready!</ToastMessage>
          <Button
            size="sm"
            inverse
            onClick={() => {
              setPage('overview');
              closeToast?.();
            }}
          >
            View Overview
          </Button>
          <CloseButton onClick={closeToast} aria-label="Close">
            ×
          </CloseButton>
        </OverviewToastContent>
      );
      toast({
        text: 'Your overview report is ready!',
        render: OverviewReadyToast,
        placement: 'bottom-end',
        autoClose: false,
        closeable: true,
      });
    }
  }, [context?.report?.done, context?.report?.overview_ready, toast, setPage]);
};
